import React, { createContext, useContext, useMemo } from "react";
import { RULES, PLAN, countAll, countInLastDays } from "../utils/planRules";
import { useUserDoc } from "../hooks/useUserDoc";
import { useActivityFeed } from "./ActivityFeedContext";
import { canGrantLungFitnessFreebieNow } from "../utils/entitlements";
const AccessControlContext = createContext(null);

/**
 * Computes recording permissions from:
 * - user doc (plan, entitlements)
 * - activity feed (usage)
 *
 * Optimistic defaults while inputs load:
 * - allow both recordings (so onboarding isn’t blocked)
 */
export function AccessControlProvider({ children }) {
  const { loading: loadingDoc, data } = useUserDoc();
  const { loading: loadingFeed, items } = useActivityFeed();

  const value = useMemo(() => {
    // ---------------------------------------------------------------------
    // !!! TESTING OVERRIDE: Set this to one of PLAN.FREE, PLAN.PERFORMANCE, etc.
    // Use PLAN.NONE to test pre-signup state. Comment out when done.
    // const TEST_PLAN_OVERRIDE = PLAN.PERFORMANCE;
    const TEST_PLAN_OVERRIDE = null;
    // ---------------------------------------------------------------------

    const loading = !!(loadingDoc || loadingFeed);
    // Plan tier / effective plan
    const tierFromDoc = data?.plan?.tier?.toLowerCase() || "";
    const tierFromEntitlement = data?.entitlements?.plan?.toLowerCase() || "";

    // 1. Determine base plan, prioritizing active entitlements/plan tier over legacy doc tier
    let planCandidate = tierFromEntitlement || tierFromDoc;

    let plan = [PLAN.FREE, PLAN.STARTER, PLAN.PERFORMANCE].includes(
      planCandidate
    )
      ? planCandidate
      : PLAN.NONE; // Default to NONE if unrecognized

    // 2. Overrule: If they have an active paid entitlement (e.g., paid subscription, founders),
    //    always treat them as PERFORMANCE.
    if (data?.entitlements?.active) {
      plan = PLAN.PERFORMANCE;
    }

    // ---------------------------------------------------------------------
    // !!! APPLY TESTING OVERRIDE
    // ---------------------------------------------------------------------
    if (
      TEST_PLAN_OVERRIDE &&
      [PLAN.NONE, PLAN.FREE, PLAN.STARTER, PLAN.PERFORMANCE].includes(
        TEST_PLAN_OVERRIDE
      )
    ) {
      plan = TEST_PLAN_OVERRIDE;
    }
    // ---------------------------------------------------------------------

    // 3. Ensure plan is a valid key if it's not NONE (only necessary if override wasn't used)
    if (
      ![PLAN.NONE, PLAN.FREE, PLAN.STARTER, PLAN.PERFORMANCE].includes(plan)
    ) {
      plan = PLAN.NONE;
    }

    const rules = RULES[plan];

    // Usage (guard if feed still loading)
    const totalAny = loading ? 0 : countAll(items);
    const recentAny30 = loading ? 0 : countInLastDays(items, 30);
    const recentExertion30 = loading
      ? 0
      : countInLastDays(items, 30, "exertion");
    const recentSpiro30 = loading
      ? 0
      : countInLastDays(items, 30, "spirometry");

    // ---- Permissions ----
    // Optimistic defaults while loading
    let canRecordExertion = true;
    let canRecordSpirometry = true;
    let reason = null;
    let upgradeTarget = null;

    if (!loading) {
      if (plan === PLAN.NONE) {
        // PLAN.NONE only allows exactly one recording (the onboarding test)
        if (rules.allowOneFirstRecording && totalAny === 0) {
          canRecordExertion = true;
          canRecordSpirometry = true;
        } else {
          canRecordExertion = false;
          canRecordSpirometry = false;
          reason = "first_recording_used";
          upgradeTarget = PLAN.FREE; // push to freemium signup
        }
      } else if (plan === PLAN.FREE) {
        // For the FREE plan, recording is always allowed (per planRules.js quotas=Infinity)
        // The restriction is only on result viewing.
        canRecordExertion = true;
        canRecordSpirometry = true;
        // If quota/cooldown logic is ever needed for FREE, it would go here.
        // Example:
        // const cooldownBlocking =
        //    (rules.cooldownDays || 0) > 0 && recentAny30 > 0;
        // if (cooldownBlocking) {
        //   canRecordExertion = false;
        //   canRecordSpirometry = false;
        //   reason = "cooldown";
        //   upgradeTarget = PLAN.STARTER;
        // }
      } else {
        // All paid/subscribed plans (STARTER, PERFORMANCE) always allow recording
        canRecordExertion = true;
        canRecordSpirometry = true;
      }
    }

    // lockResults is defined in RULES, e.g., true for FREE, false for PERFORMANCE
    const lockResults = !!rules.lockResults;

    // Freebie availability (only matters on free + when viewing spirometry results)
    const canSeeLungFitnessThisMonth =
      plan !== PLAN.FREE ? true : canGrantLungFitnessFreebieNow(data);

    return {
      loading,
      plan,
      rules,
      canRecord: {
        exertion: canRecordExertion,
        spirometry: canRecordSpirometry,
      },
      lockResults,
      freebies: {
        lungFitnessAvailable: canSeeLungFitnessThisMonth,
      },
      reason,
      upgradeTarget,
      totalAny,
      recentAny30,
    };
    // Depend only on the inputs that actually change
  }, [loadingDoc, loadingFeed, data, items]);

  return (
    <AccessControlContext.Provider value={value}>
      {children}
    </AccessControlContext.Provider>
  );
}

export function useAccessControlState() {
  const ctx = useContext(AccessControlContext);
  if (!ctx)
    throw new Error(
      "useAccessControlState must be used within AccessControlProvider"
    );
  return ctx;
}
